/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, from, throwError } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  shareReplay,
  toArray,
} from 'rxjs/operators';
import { CustomerApiService } from './customer-api.service';

const ENDPOINTS = {
  groupDelete: '/v1/search_query/group/delete',
  groupRename: '/v1/search_query/group/rename',
  groupNew: '/v1/search_query/group/new',
  groupView: '/v1/search_query/group/view',
  groupResolve: '/v1/search_query/group/resolve',
  get: '/v1/search_query/get',
  save: '/v1/search_query/save',
  saveArray: '/v1/search_query/save_array',
  delete: '/v1/search_query',
  assignToGroup: '/v1/search_query/assign_to_group',
  removeFromGroup: '/v1/search_query/remove_from_group',
  resolve: '/v1/search_query/resolve',
} as const;

/** Pseudo-collection id for the queries that belong to no collection. */
export const OTHER_COLLECTION_ID = 'other';

@Injectable({
  providedIn: 'root',
})
export class QueryApiService {
  cachedQueries: any;
  cachedCollections: any;
  queriesChange = true;
  collectionsChange = true;

  /** Queries in no collection, as of the last query-guard run ("Other" tab). */
  otherQueries: any[] = [];

  // Reactive source of truth for the query-domain selection state (was the
  // `query`/`querys`/`iquery`/`queryForNewsfeed` god-bag on UserStateService).
  // Writers assign whole objects through the setters → the subject emits.
  private readonly _query$ = new BehaviorSubject<any>(undefined);
  private readonly _querys$ = new BehaviorSubject<any>(undefined);
  private readonly _iquery$ = new BehaviorSubject<any>(undefined);
  private readonly _queryForNewsfeed$ = new BehaviorSubject<any>(undefined);

  readonly query$: Observable<any> = this._query$.asObservable();
  readonly querys$: Observable<any> = this._querys$.asObservable();
  readonly iquery$: Observable<any> = this._iquery$.asObservable();
  readonly queryForNewsfeed$: Observable<any> =
    this._queryForNewsfeed$.asObservable();

  /** Selected query (single query or synthetic group/collection query). */
  get query(): any {
    return this._query$.value;
  }
  set query(v: any) {
    this._query$.next(v);
  }

  /** All collections (query groups). */
  get querys(): any {
    return this._querys$.value;
  }
  set querys(v: any) {
    this._querys$.next(v);
  }

  /** Queries within the active collection. */
  get iquery(): any {
    return this._iquery$.value;
  }
  set iquery(v: any) {
    this._iquery$.next(v);
  }

  /** Transient per-view override: the query a "view in newsfeed" toggle picked. */
  get queryForNewsfeed(): any {
    return this._queryForNewsfeed$.value;
  }
  set queryForNewsfeed(v: any) {
    this._queryForNewsfeed$.next(v);
  }

  constructor(private api: CustomerApiService) {}

  /**
   * Reset the fetch caches. Mutating methods call this internally so the next
   * read refetches. NOTE: this must NOT touch the selection subjects above —
   * mutations (add/delete query) should not wipe the active selection.
   */
  invalidateCache(): void {
    this.queriesChange = true;
    this.collectionsChange = true;
    this.resolveCache.clear();
  }

  /** Clear the selection state on session end (AuthService.authLogout). */
  resetState(): void {
    this._query$.next(undefined);
    this._querys$.next(undefined);
    this._iquery$.next(undefined);
    this._queryForNewsfeed$.next(undefined);
    this.otherQueries = [];
    this.resolveCache.clear();
  }

  // GROUPS -------------------------------------------------------------------

  deleteGroupQuery(uid: string): Observable<any> {
    this.invalidateCache();
    return this.api.get(ENDPOINTS.groupDelete, { uid });
  }

  editGroupQuery(name: string, uid: string): Observable<any> {
    this.invalidateCache();
    return this.api.get(ENDPOINTS.groupRename, { uid, new_name: name });
  }

  addGroupQuery(name: string): Observable<any> {
    this.invalidateCache();
    return this.api.get(ENDPOINTS.groupNew, { name });
  }

  getGroupsQuery(): Observable<any> {
    if (!this.collectionsChange && this.cachedCollections) {
      return of(this.cachedCollections);
    }
    return this.api.get<any>(ENDPOINTS.groupView).pipe(
      map((collections) => {
        this.collectionsChange = false;
        this.cachedCollections = JSON.parse(JSON.stringify(collections));
        return collections;
      }),
      shareReplay(),
    );
  }

  // QUERIES ------------------------------------------------------------------

  getQuery(): Observable<any> {
    if (!this.queriesChange && this.cachedQueries) {
      return of(this.cachedQueries);
    }
    this.queriesChange = false;
    return this.api.get<any>(ENDPOINTS.get).pipe(
      map((queries) => {
        this.cachedQueries = JSON.parse(JSON.stringify(queries));
        return queries;
      }),
      shareReplay(),
    );
  }

  getQueryGroup(id: string): Observable<any> {
    return this.api.get(ENDPOINTS.get, { group: id });
  }

  addQuery(data: any): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.save, data);
  }

  addQueries(data: any): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.saveArray, data);
  }

  delQueries(idsArray: string[]): Observable<any> {
    this.invalidateCache();
    return this.api.deleteWithBody(ENDPOINTS.delete, { uids: idsArray });
  }

  // GROUP ASSIGNMENT ---------------------------------------------------------

  addQueriesToGroup(queriesIds: string[], groupId: string): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.assignToGroup, {
      group: groupId,
      query_ids: queriesIds,
    });
  }

  removeQueriesFromGroup(
    queriesIds: string[],
    groupId: string,
  ): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.removeFromGroup, {
      group: groupId,
      query_ids: queriesIds,
    });
  }

  // RESOLUTION ---------------------------------------------------------------

  // Same lifecycle as cachedQueries/cachedCollections: cached until a mutation
  // calls invalidateCache(). The query chart page resolves the same uid 3-6
  // times per refresh (chart sentiment, title, mosaic, ...) — they share one
  // GET. Errors are never cached, so a failed resolve retries.
  private resolveCache = new Map<string, Observable<any>>();

  resolveQuery(uid: string): Observable<any> {
    return this.cachedResolve(`query:${uid}`, () =>
      this.api.get(ENDPOINTS.resolve, { uid }),
    );
  }

  resolveQueryGroup(uid: string): Observable<any> {
    if (uid === OTHER_COLLECTION_ID) {
      // Not a backend group: resolve its queries a few at a time. A query that
      // fails to resolve (e.g. it embeds a deleted portfolio) is left out
      // rather than blanking the whole tab.
      return this.cachedResolve(`group:${uid}`, () =>
        from(this.otherQueries).pipe(
          mergeMap(
            (q) => this.resolveQuery(q.uid).pipe(catchError(() => of(null))),
            4,
          ),
          toArray(),
          map((resolved) => ({ uid, content: resolved.filter(Boolean) })),
        ),
      );
    }
    return this.cachedResolve(`group:${uid}`, () =>
      this.api.get(ENDPOINTS.groupResolve, { uid }),
    );
  }

  private cachedResolve(
    key: string,
    request: () => Observable<any>,
  ): Observable<any> {
    const cached = this.resolveCache.get(key);
    if (cached) {
      return cached;
    }
    const shared$ = request().pipe(
      catchError((err) => {
        this.resolveCache.delete(key);
        return throwError(() => err);
      }),
      shareReplay(1),
    );
    this.resolveCache.set(key, shared$);
    return shared$;
  }
}
